// ThemeProvider: aplica `data-theme` no <html> e persiste a preferência (só conveniência local).
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  THEME_STORAGE_KEY,
  type Theme,
  ThemeContext,
  type ThemeContextValue,
  type ThemeHost,
  browserThemeHost,
  readStoredTheme,
  resolveTheme,
} from './theme';

export interface ThemeProviderProps {
  readonly host?: ThemeHost;
  readonly children: ReactNode;
}

export function ThemeProvider({ host, children }: ThemeProviderProps) {
  const [effectiveHost] = useState<ThemeHost>(() => host ?? browserThemeHost());
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme(effectiveHost.storage));
  const resolved = resolveTheme(theme, effectiveHost.prefersDark());

  useEffect(() => {
    effectiveHost.apply(resolved);
  }, [effectiveHost, resolved]);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      try {
        effectiveHost.storage?.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // conveniência local
      }
    },
    [effectiveHost],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolved, setTheme }),
    [theme, resolved, setTheme],
  );
  return <ThemeContext value={value}>{children}</ThemeContext>;
}
